import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "./api";
import T from "./i18n";
import themes from "./constants/themes";
import Ctx from "./contexts/AppContext";
import usePullToRefresh from "./hooks/usePullToRefresh";
import { normNotifs } from "./utils/notifications";
import { registerServiceWorker, enablePushNotifications, isPushSupported } from "./utils/pushSubscribe";

// --- Layout Components ---
import LoginPage from "./components/layout/LoginPage";
import Onboarding, { isOnboarded } from "./components/layout/Onboarding";
import TopBar from "./components/layout/TopBar";
import BottomBar from "./components/layout/BottomBar";
import SharePropertiesModal from "./components/landlord/SharePropertiesModal";
import CampaignDisplay from "./components/common/CampaignDisplay";

// --- Role Page Routers ---
import LandlordPage from "./components/landlord/LandlordPage";
import TenantPage from "./components/tenant/TenantPage";
import ServicePage from "./components/service/ServicePage";
import AdminPage from "./components/admin/AdminPage";
import PublicBrowsePage from "./components/public/PublicBrowsePage";
import FeedbackButton from "./components/common/FeedbackButton";

// --- Shared UI ---
import Noti from "./components/common/Noti";
import ConfirmModal from "./components/common/ConfirmModal";
import ColdStartLoader from "./components/common/ColdStartLoader";
import ProductTour from "./components/common/ProductTour";
import useFtue from "./hooks/useFtue";
import stepsForRole from "./constants/ftueSteps";
import CoachMarkTour from "./components/common/CoachMarkTour";
import useCoachMarkTour from "./hooks/useCoachMarkTour";
import coachStepsForRole from "./constants/coachMarkSteps";
import usePageCoachTour from "./hooks/usePageCoachTour";
import { getPageCoachSteps } from "./constants/pageCoachSteps";
import DesktopSidebar from "./components/layout/DesktopSidebar";
import { IS_DESKTOP } from "./constants/platform";

// --- Desktop web (ProRentz / Hostinger build) ---
// IS_DESKTOP (shared, see constants/platform) is true in the desktop
// build or when previewing with ?desktop=1. On a normal mobile load it's
// false, so every desktop branch below is skipped — mobile UX unchanged.
// DesktopApp lazy so the mobile main bundle never grows for the site.
const DesktopApp = React.lazy(() => import("./desktop/DesktopApp"));
const DesktopLogin = React.lazy(() => import("./desktop/DesktopLogin"));

const initData = {
  props: [{ id: "p1", addr: "12, Gandhi Nagar, Sivakasi", rooms: 2, rent: 8000, dep: 16000, amen: ["Water", "WiFi"], occ: true, tid: "t1" }, { id: "p2", addr: "45, Thiruvalluvar St, Sivakasi", rooms: 3, rent: 12000, dep: 24000, amen: ["AC", "Water", "WiFi"], occ: true, tid: "t2" }, { id: "p3", addr: "78, Meenakshi Nagar, Sivakasi", rooms: 1, rent: 5000, dep: 10000, amen: ["Water"], occ: false, tid: null }, { id: "p4", addr: "23, Collector Office Rd, Sivakasi", rooms: 2, rent: 10000, dep: 20000, amen: ["AC", "Water"], occ: true, tid: "t3" }],
  tens: [{ id: "t1", name: "Murugan K", ph: "+91 98765 43210", aad: "4521", join: "2024-06-15", pid: "p1", vac: false }, { id: "t2", name: "Lakshmi S", ph: "+91 87654 32109", aad: "7832", join: "2024-03-01", pid: "p2", vac: false }, { id: "t3", name: "Ravi M", ph: "+91 76543 21098", aad: "1256", join: "2025-01-10", pid: "p4", vac: true }],
  pays: [{ id: "y1", tid: "t1", amt: 8000, com: 500, dt: "2025-02-01", mth: "upi", st: "paid", mo: "Feb 25" }, { id: "y2", tid: "t1", amt: 8000, com: 450, dt: "2025-01-03", mth: "cash", st: "paid", mo: "Jan 25" }, { id: "y3", tid: "t2", amt: 12000, com: 500, dt: "2025-02-01", mth: "upi", st: "paid", mo: "Feb 25" }, { id: "y4", tid: "t2", amt: 12000, com: 450, dt: "2025-01-05", mth: "bank", st: "paid", mo: "Jan 25" }, { id: "y5", tid: "t3", amt: 10000, com: 500, dt: "", mth: "", st: "due", mo: "Feb 25" }],
  maints: [{ id: "m1", tid: "t1", desc: "Bathroom tap leaking", urg: "high", st: "open", dt: "2025-02-03" }, { id: "m2", tid: "t2", desc: "Fan not working in bedroom", urg: "med", st: "assigned", dt: "2025-01-28" }, { id: "m3", tid: "t1", desc: "Wall paint peeling", urg: "low", st: "done", dt: "2025-01-15" }],
  ccs: [{ id: "c1", mo: "Feb 2025", eb: 3000, cl: 1500, n: 3 }, { id: "c2", mo: "Jan 2025", eb: 2700, cl: 1500, n: 3 }],
  msgs: [{ id: "g1", pid: "p1", fr: "ll", tx: "Rent due on 1st, pay via UPI", tm: "9:00" }, { id: "g2", pid: "p1", fr: "t1", tx: "Paid via UPI, please check", tm: "10:30" }, { id: "g3", pid: "p2", fr: "ll", tx: "Maintenance team visits tomorrow", tm: "14:00" }]
};

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [dk, setDk] = useState(() => localStorage.getItem("dk") === "true");
  const [role, setRole] = useState(() => localStorage.getItem("role") || null);
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem("loggedIn") === "true");
  const [page, setPage] = useState(() => window.history.state?.page || "dash");
  const [publicPage, setPublicPage] = useState(null);
  const [initialPropertyId, setInitialPropertyId] = useState(null);
  // Desktop only: logged-out shows the ProRentz marketing site; this
  // flips to the in-app LoginPage when the user clicks Sign in. Unused
  // on mobile (IS_DESKTOP false).
  const [showLogin, setShowLogin] = useState(false);
  const [onboarded, setOnboarded] = useState(isOnboarded);
  // ?onboarding=1 replays the intro at any time (handy for review/QA).
  const [obPreview, setObPreview] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("onboarding") === "1"; } catch (_) { return false; }
  });
  // Desktop only: which DesktopApp view to open on (re)mount. The login's
  // "Browse Properties" sets it to "browse" so leaving the login lands on
  // the marketplace rather than the marketing home.
  const [desktopView, setDesktopView] = useState("home");
  const [selPropId, setSelPropId] = useState(null);
  const [selTid, setSelTid] = useState(null);
  // Pending intent flag: when set, the next mount of SettingsPage opens
  // the UpgradeModal automatically. Lets a deep-link CTA (e.g. the red
  // expired banner on the dashboard) skip the "land on Settings, find
  // Renew button" step and jump straight to the modal.
  const [openUpgradeOnSettings, setOpenUpgradeOnSettings] = useState(false);
  const [props, setProps] = useState([]);
  const [tens, setTens] = useState([]);
  const [pays, setPays] = useState([]);
  const [mts, setMts] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [notif, setNotif] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [currUserId, setCurrUserId] = useState(() => localStorage.getItem("currUserId") || null);
  const [dbErr, setDbErr] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true);

  const [openChat, setOpenChat] = useState(null);
  // Empty defaults — referenced anywhere we need to "clear" settings
  // (logout, user-switch, sync error) so we don't leak the previous
  // user's keys into the next user's React state.
  const EMPTY_SETTINGS = { upi_id: "", upi_name: "", bank_details: "", admin_password: "", agreement_terms: "", landlord_signature: "" };
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [editingCC, setEditingCC] = useState(null);
  const [sps, setSps] = useState([]);
  const [units, setUnits] = useState([]);
  const [selMaintId, setSelMaintId] = useState(null);
  const [leases, setLeases] = useState([]);
  const [selLeaseId, setSelLeaseId] = useState(null);
  const [plan, setPlan] = useState({ type: "free", maxProperties: 3, currentCount: 0 });
  const [selOrgId, setSelOrgId] = useState(null);
  // Phase 17: currently-selected vacate request for the landlord-side
  // detail view. Lives in App state so the inbox → detail navigation
  // doesn't need to round-trip through URL params.
  const [selVacateId, setSelVacateId] = useState(null);
  const [selVacateRequest, setSelVacateRequest] = useState(null);
  const [orgId, setOrgId] = useState(() => localStorage.getItem("orgId") || null);
  const [userName, setUserName] = useState("");
  // Logged-in user's own profile picture (DP). Populated from the sync
  // response (data.userAvatar); falls back to the Google social-login
  // picture. Consumed by the TopBar avatar + exposed via ctxVal.
  const [userAvatar, setUserAvatar] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgRole, setOrgRole] = useState("owner");
  // Global app-level settings, echoed from the server on every /sync.
  // Currently just the feedback FAB on/off switch — extend the shape
  // when more cross-client toggles land. Default to FAB enabled so a
  // pre-sync render still shows the bug button (matches prior
  // always-on behavior); the server is authoritative once /sync lands.
  const [appSettings, setAppSettings] = useState({ feedback_button_enabled: true });

  // Global "Share Properties" modal — opened from the bottom-bar center
  // Share button (landlord). Lives at App level so it overlays the whole
  // screen regardless of which page is active.
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Persist language preference
  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => {
    localStorage.setItem("dk", dk ? "true" : "false");
    const th = dk ? themes.dark : themes.light;
    document.body.style.background = th.bg;
    document.body.style.color = th.tx;
    // CSS variables consumed by App.css's `select option` rule. Browsers
    // render the OS dropdown panel outside our React tree, so inline
    // styles on <select> never reach it. Setting --opt-bg/--opt-fg here
    // gives EVERY <option> in the app (17+ files) the right palette
    // without per-file changes. h.cd is the card colour (deep dark in
    // dark mode, white in light mode); h.tx is the primary text colour.
    document.documentElement.style.setProperty('--opt-bg', th.cd);
    document.documentElement.style.setProperty('--opt-fg', th.tx);
    // CRITICAL: on Chromium/Windows the <select> dropdown panel is
    // rendered by the OS, not the browser — CSS on <option> is ignored.
    // Setting color-scheme on :root tells Chromium "render YOUR own
    // dark picker instead of delegating to Windows," and that
    // browser-rendered picker DOES honour the option CSS. color-scheme
    // is inherited so every form control on the page picks it up.
    document.documentElement.style.colorScheme = th.dk ? 'dark' : 'light';
  }, [dk]);

  // Register the service worker once on app boot. Idempotent — safe even
  // if the user isn't logged in yet (the SW just sits idle until a push
  // arrives, and pushes only arrive once the user has subscribed).
  useEffect(() => {
    if (!isPushSupported()) return;
    registerServiceWorker().catch(() => {});
  }, []);

  // Handle Razorpay redirect-mode landings: when the iframe checkout
  // can't render, Razorpay redirects to /api/billing/checkout-return,
  // which (after verifying the signature) sends the user back here
  // with ?upgrade=success&plan=<id> or ?upgrade=failed&reason=<code>.
  // Surface the result as a flash and strip the params so a refresh
  // doesn't re-show the same toast.
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);

      // ?property=PROP_ID — shared property deep-link.
      // Opens the public browse page and auto-selects the property so
      // anyone who taps the share link lands directly on that listing.
      const propId = qs.get('property');
      if (propId) {
        setPublicPage("browse");
        setInitialPropertyId(propId);
        const hash = window.location.hash || '';
        window.history.replaceState({}, document.title, window.location.pathname + hash);
        return; // skip upgrade param handling on same load
      }

      // ?rent=success|failed — Cashfree online-rent redirect return.
      // The server (/api/payments/cashfree-return) already verified and
      // settled the payment; we just flash the result. A full-page
      // reload happened on the redirect, so the next sync shows the row
      // as paid — the flash is immediate feedback.
      const r = qs.get('rent');
      if (r) {
        if (r === 'success') {
          flashRef.current?.('Payment successful! Your rent has been paid.');
        } else if (r === 'failed') {
          const reason = qs.get('reason') || 'unknown';
          flashRef.current?.(`Payment failed: ${reason.replace(/_/g, ' ')}`, 'error');
        }
        const hashR = window.location.hash || '';
        window.history.replaceState({}, document.title, window.location.pathname + hashR);
        return;
      }

      const u = qs.get('upgrade');
      if (!u) return;
      if (u === 'success') {
        const plan = qs.get('plan') || '';
        flashRef.current?.(plan ? `${plan.toUpperCase()} plan activated!` : 'Plan activated!');
      } else if (u === 'failed') {
        const reason = qs.get('reason') || 'unknown';
        flashRef.current?.(`Payment failed: ${reason.replace(/_/g, ' ')}`, 'error');
      }
      // Strip the upgrade params but preserve the hash route so the
      // user lands where they were headed.
      const hash = window.location.hash || '';
      window.history.replaceState({}, document.title, window.location.pathname + hash);
    } catch (_) { /* ignore */ }
  }, []);

  // After login: if the user has already granted notification permission
  // on this device (i.e. they enabled it before and just came back), make
  // sure we have a fresh subscription registered against the server with
  // the *current* user identity. Without this, a tenant logging in on a
  // browser where a previous tenant subscribed would get the wrong pushes.
  useEffect(() => {
    if (!loggedIn) return;
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Re-subscribe quietly — won't prompt because permission is already granted.
    enablePushNotifications().catch(() => {});
  }, [loggedIn, currUserId]);

  // Initial Data Fetch — re-runs on login/logout
  useEffect(() => {
    // Not logged in — nothing to sync
    if (!loggedIn) { setIsSyncing(false); return; }
    // If marked as logged in but no auth token, force logout immediately
    if (!localStorage.getItem('auth_token')) {
      setLoggedIn(false); setRole(null); setCurrUserId(null); setIsSyncing(false);
      localStorage.removeItem('loggedIn'); localStorage.removeItem('role'); localStorage.removeItem('currUserId');
      return;
    }
    // Admin and social user roles skip sync
    const currentRole = localStorage.getItem('role');
    if (currentRole === 'admin' || currentRole === 'user') { setIsSyncing(false); return; }
    // Show loading spinner for fresh login
    setIsSyncing(true);
    const syncData = async () => {
      try {
        const data = await api.sync();
        if (data) {
          if (data.dbError) {
            setDbErr(data.dbError);
            return;
          }
          setDbErr(null);
          setProps(data.props || []);
          setTens(data.tens || []);
          setPays(data.pays || []);
          setMts(data.maints || []);
          setCcs(data.ccs || []);
          setMsgs(data.msgs || []);
          setNotifs(normNotifs(data.notifs));
          setSps(data.sps || []);
          setUnits(data.units || []);
          setLeases(data.leases || []);
          if (data.plan) setPlan(data.plan);
          if (data.userName) setUserName(data.userName);
          {
            // Own DP: prefer the server-computed avatar (tenant/service
            // own row, or landlord's settings blob); fall back to the
            // Google social-login picture when no DP is set.
            const _g = (() => { try { return JSON.parse(localStorage.getItem("googleUser") || "null"); } catch { return null; } })();
            setUserAvatar(data.userAvatar || (_g && _g.picture) || "");
          }
          if (data.orgName) setOrgName(data.orgName);
          if (data.orgRole) setOrgRole(data.orgRole);
          if (data.appSettings) setAppSettings(prev => ({ ...prev, ...data.appSettings }));
        } else if (!localStorage.getItem('auth_token')) {
          // Sync returned null due to 401 — force logout
          setLoggedIn(false); setRole(null); setCurrUserId(null);
        }
        // SECURITY: replace, never merge into prev. Merging carried the
        // previous user's keys (UPI id, bank details, landlord_info)
        // into the current user's state when the previous user had been
        // logged in earlier on the same browser/app session. Now we
        // unconditionally reset to empty defaults and overlay only what
        // the server returned for THIS user — even an empty response
        // wipes any stale data that a prior session left behind.
        const setts = await api.getSettings();
        setSettings({ ...EMPTY_SETTINGS, ...(setts || {}) });
      } catch (e) {
        console.error("Failed to fetch data or settings", e);
      } finally {
        setIsSyncing(false);
      }
    };
    syncData();
  }, [loggedIn]);

  // Poll for new data (notifications, messages, etc.) every 30 seconds — skip for admin
  // Refs so the interval closure always sees the latest role/currUserId/flash
  // without having to tear down + restart the setInterval on every state change.
  const roleRef = useRef(role); useEffect(() => { roleRef.current = role; }, [role]);
  const currUserIdRef = useRef(currUserId); useEffect(() => { currUserIdRef.current = currUserId; }, [currUserId]);
  const flashRef = useRef(null);
  // Track live login state for the listeners below (effects with [] deps).
  const loggedInRef = useRef(loggedIn); useEffect(() => { loggedInRef.current = loggedIn; }, [loggedIn]);
  // Session expiry: api.js clears localStorage on any 401 and fires this
  // event. Route to the login screen instead of leaving a dead UI.
  useEffect(() => {
    const onExpired = () => {
      if (!loggedInRef.current) return; // e.g. a failed login attempt — not an expiry
      setLoggedIn(false); setRole(null); setCurrUserId(null);
      if (flashRef.current) flashRef.current("Session expired — please log in again", "error");
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);
  useEffect(() => {
    const interval = setInterval(async () => {
      // Live gate — the old mount-time `if (role === 'admin'...)` froze the
      // role: a landlord logging in after an admin session never got polling,
      // and a logged-out browser polled /sync with `Bearer null` forever.
      const r = roleRef.current;
      if (!r || r === 'admin' || r === 'user') return;
      if (!localStorage.getItem('auth_token')) return;
      try {
        const data = await api.sync();
        if (data && !data.dbError) {
          setMsgs(data.msgs || []);
          setNotifs(prev => {
            const server = normNotifs(data.notifs);
            const localOnly = prev.filter(p => !server.find(s => s.id === p.id));
            // Detect newly arrived unread notifications targeted at the current
            // user so we can surface a toast — the bell badge alone is easy to miss
            // (esp. for landlords while they're on another tab/page).
            const r = roleRef.current;
            const uid = currUserIdRef.current;
            const isMine = (n) => {
              if (r === 'landlord') return n.forRole === 'landlord';
              if (r === 'tenant')   return n.forRole === 'tenant'  && n.forTid === uid;
              if (r === 'service')  return n.forRole === 'service' && n.forTid === uid;
              if (r === 'admin')    return n.forRole === 'admin';
              return false;
            };
            const fresh = server.filter(s => !s.read && isMine(s) && !prev.find(p => p.id === s.id));
            if (fresh.length > 0 && flashRef.current) {
              const msg = fresh.length === 1
                ? `🔔 ${fresh[0].msg}`
                : `🔔 ${fresh.length} new notifications`;
              flashRef.current(msg, "info");
            }
            return [...localOnly, ...server];
          });
          setMts(data.maints || []);
          setPays(data.pays || []);
          setCcs(data.ccs || []);
          setLeases(data.leases || []);
        }
      } catch (e) { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper Wrappers for API Persistence
  const updateProps = (newProps) => { setProps(newProps); newProps.forEach(p => api.saveProperty(p)); };
  const updatePays = (newPays) => { setPays(newPays); newPays.forEach(p => api.savePayment(p)); };

  const go = (p) => { window.scrollTo(0, 0); setPage(p); window.history.pushState({ page: p }, "", `#${p}`); };

  useEffect(() => {
    const handlePopState = (e) => { setPage(e.state?.page || "dash"); };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // FIX #6: Use functional updater to avoid stale closure
  const addNotif = (tRole, tId, msg, type) => {
    const newNotif = { id: `n${Date.now()}`, forRole: tRole, forTid: tId, msg, type, time: new Date().toLocaleTimeString(), read: false };
    setNotifs(prev => [newNotif, ...prev]);
    api.saveNotification(newNotif);
  };

  // FIX #21: Use toast-style notification instead of blocking alert()
  const flashTimerRef = useRef(null);
  const flash = (msg, type = "success") => {
    setNotif({ m: msg, type });
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setNotif(null), 3000);
  };
  // Expose `flash` to the polling effect (defined earlier via forward ref)
  flashRef.current = flash;

  // Reusable confirmation modal state (replaces window.confirm)
  const [confirmAction, setConfirmAction] = useState(null);
  const showConfirm = (message, onConfirm) => setConfirmAction({ message, onConfirm });
  const closeConfirm = () => setConfirmAction(null);

  // Re-sync helper for after mutations
  const refreshData = async () => {
    try {
      const data = await api.sync();
      if (data && !data.dbError) {
        setProps(data.props || []); setTens(data.tens || []); setPays(data.pays || []);
        setMts(data.maints || []); setCcs(data.ccs || []); setMsgs(data.msgs || []);
        setNotifs(normNotifs(data.notifs)); setSps(data.sps || []); setLeases(data.leases || []);
        if (data.plan) setPlan(data.plan);
        if (data.orgName) setOrgName(data.orgName);
      }
      // SECURITY: also re-fetch settings on every refresh. Without this, a
      // multi-org tenant who switches tenancies (e.g. Default → Bharathi)
      // sees the FIRST org's UPI/bank details on Pay Now until they fully
      // log out — because settings React state was only populated by the
      // initial-login useEffect and never refreshed on switchOrg / manual
      // refresh. Always overwrite to defeat stale-state leaks.
      const setts = await api.getSettings();
      setSettings({ ...EMPTY_SETTINGS, ...(setts || {}) });
    } catch (e) { console.error("Refresh failed:", e); }
  };

  const doLogout = () => {
    // Clear all per-user cached settings (one key per user_id) so the
    // next user on this browser can't read back the previous user's
    // landlord/payment data — that was the source of a real cross-user
    // leak where a tenant unrelated to landlord A still saw A's UPI
    // and bank details after a Render cold-start fallback.
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('cached_settings')) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch (_) { /* defensive — never block logout on this */ }
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("role");
    localStorage.removeItem("currUserId");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("googleUser");
    localStorage.removeItem("orgId");
    // Clear any property deep-link overlay state — otherwise a user who
    // arrived via ?property= and later logs out lands on the browse page
    // instead of the login screen.
    setPublicPage(null);
    setInitialPropertyId(null);
    setShowLogin(false);   // desktop: drop back to the marketing site
    // Reset all server-derived React state so a fresh user-switch (no
    // page reload) cannot inherit the previous user's tenants /
    // properties / payments / settings / etc. Without this, switching
    // users in the same SPA session showed cross-user data even though
    // the server / localStorage had been cleared.
    setSettings(EMPTY_SETTINGS);
    setProps([]); setTens([]); setPays([]); setMts([]); setCcs([]);
    setMsgs([]); setNotifs([]); setSps([]); setUnits([]); setLeases([]);
    setUserName(""); setUserAvatar(""); setOrgName(""); setOrgRole("");
    api.logout();
    setLoggedIn(false);
    setRole(null);
    setCurrUserId(null);
    setOrgId(null);
    // Clear all user data on logout — prevents any stale data leaking to next session
    setNotifs([]);
    setProps([]);
    setTens([]);
    setPays([]);
    setMts([]);
    setCcs([]);
    setMsgs([]);
    setSps([]);
    setLeases([]);
    setIsSyncing(false); // Reset so next login doesn't get stuck
    go("dash");
  };

  // Pull-to-refresh
  const { handlers: pullHandlers, pulling, pullY } = usePullToRefresh(async () => {
    await refreshData();
    flash(T[lang].refreshed);
  });

  // First-time user experience — auto-launches a role-specific tour
  // the first time a landlord / tenant / service-person logs in.
  // Stored per user id so a returning user never sees it again, and
  // a different role logging into the same browser gets its own.
  // Admin role is skipped (super-admin doesn't need an onboarding
  // walk-through). MUST be defined before ctxVal so replayTours
  // can be put on the context object.
  const ftueRole = (role === "landlord" || role === "tenant" || role === "service") ? role : null;
  const ftue = useFtue(ftueRole, currUserId);
  // Coach-mark tour runs AFTER the modal product tour completes —
  // hands-on guidance pointing at actual nav tabs and key buttons.
  // Separate localStorage key so each tour completes independently.
  const coachTour = useCoachMarkTour(ftueRole, currUserId);
  // Per-page coach mark tour. Watches the current `page` and auto-
  // launches a contextual tour the first time the user visits each
  // section (Dashboard / Properties / Tenants / Payments /
  // Maintenance / etc). Per-user + per-page persistence in
  // localStorage. Gated on the main FTUE completion flag so the
  // brand-new-user experience doesn't pile three onboarding
  // layers on top of each other.
  const currentPageSteps = ftueRole ? getPageCoachSteps(page, ftueRole) : [];
  const pageCoach = usePageCoachTour({
    page,
    role: ftueRole,
    userId: currUserId,
    hasSteps: currentPageSteps.length > 0,
    gatedOnFtueKey: ftueRole && currUserId ? `ftue_${ftueRole}_${currUserId}` : null,
  });

  // Combined replay — exposed via context so the gear-menu "Show
  // Tour Again" item (in TopBar) can trigger both onboarding
  // layers at once without each consumer needing to know about
  // the two separate hooks. The per-page tours are NOT reset by
  // this — page tours have their own help-icon entry point.
  const replayTours = useCallback(() => {
    if (!ftueRole) return;
    try { ftue.replay?.(); } catch (_) {}
    try { coachTour.replay?.(); } catch (_) {}
  }, [ftueRole, ftue, coachTour]);

  // Replay just the current page's tour — wired into a tiny help
  // affordance the user can reach from anywhere.
  const replayPageTour = useCallback(() => {
    try { pageCoach.replay?.(); } catch (_) {}
  }, [pageCoach]);

  const ctxVal = { t: T[lang], h: dk ? themes.dark : themes.light, lang, setLang, dk, setDk, role, setRole, loggedIn, setLoggedIn, page, go, props, setProps, tens, setTens, pays, setPays, mts, setMts, ccs, setCcs, msgs, setMsgs, notifs, setNotifs, currUserId, setCurrUserId, selPropId, setSelPropId, selTid, setSelTid, openChat, setOpenChat, flash, addNotif, settings, setSettings, editingCC, setEditingCC, doLogout, showConfirm, closeConfirm, sps, setSps, units, setUnits, selMaintId, setSelMaintId, leases, setLeases, selLeaseId, setSelLeaseId, refreshData, plan, selOrgId, setSelOrgId, setPublicPage, orgId, setOrgId, userName, userAvatar, setUserAvatar, orgName, orgRole, openUpgradeOnSettings, setOpenUpgradeOnSettings, selVacateId, setSelVacateId, selVacateRequest, setSelVacateRequest, replayTours, replayPageTour, pageHasCoachTour: currentPageSteps.length > 0, appSettings, setAppSettings, shareModalOpen, setShareModalOpen };

  if (dbErr) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f9ff", padding: "20px", textAlign: "center", flexDirection: "column" }}>
        <div style={{ background: "#fee2e2", padding: "30px", borderRadius: "16px", border: "2px solid #ef4444", maxWidth: "400px", boxShadow: "0 10px 25px rgba(239, 68, 68, 0.2)" }}>
          <h2 style={{ color: "#991b1b", marginTop: 0, fontSize: "22px" }}>{"⚠️ " + T[lang].dbConnError}</h2>
          <p style={{ color: "#7f1d1d", fontSize: "14px", fontWeight: "500", lineHeight: "1.5" }}>{T[lang].dbConnDesc}</p>
          <code style={{ display: "block", background: "#fef2f2", padding: "12px", borderRadius: "8px", color: "#b91c1c", marginTop: "15px", fontSize: "12px", wordWrap: "break-word", border: "1px dashed #fca5a5" }}>{dbErr}</code>
          <button onClick={() => window.location.reload()} style={{ marginTop: "25px", width: "100%", padding: "14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", transition: "0.2s" }}>{T[lang].refreshConn}</button>
        </div>
      </div>
    );
  }

  // Shared ?property= deep link for LOGGED-IN users — previously only the
  // logged-out branch rendered the browse page, so tapping a share link
  // while signed in silently did nothing (the URL param was already
  // stripped). Back returns to the normal dashboard.
  if (loggedIn && publicPage === "browse" && initialPropertyId) {
    return <Ctx.Provider value={ctxVal}><PublicBrowsePage onBack={() => { setPublicPage(null); setInitialPropertyId(null); }} isLoggedIn={true} initialPropertyId={initialPropertyId} /></Ctx.Provider>;
  }
  if (!loggedIn && publicPage === "browse") return <Ctx.Provider value={ctxVal}><PublicBrowsePage onBack={() => { setPublicPage(null); setInitialPropertyId(null); }} initialPropertyId={initialPropertyId} /></Ctx.Provider>;
  // Desktop, logged out → ProRentz marketing site (landing + marketplace).
  // "Sign in" flips showLogin so the existing LoginPage renders next.
  if (IS_DESKTOP && !loggedIn && !showLogin) {
    return <Ctx.Provider value={ctxVal}><React.Suspense fallback={null}><DesktopApp onSignIn={() => setShowLogin(true)} initialView={desktopView} /></React.Suspense></Ctx.Provider>;
  }
  // Desktop gets a purpose-built, unified split-card auth screen
  // (DesktopLogin) — one elevated card fusing the brand panel and the
  // form, reusing the same api + context as LoginPage but native to the
  // web. Mobile/APK render the shared LoginPage bare (untouched).
  // Mobile first run → role-first onboarding before the login screen.
  // Picking a role here pre-selects it on LoginPage, so the user lands
  // straight on their own sign-in form instead of the role chooser.
  // Desktop is exempt: it already has the marketing site as its front door.
  if (obPreview || (!loggedIn && !IS_DESKTOP && !onboarded)) {
    return <Ctx.Provider value={ctxVal}><Onboarding onDone={(r) => {
      // Never re-role a signed-in user who is just previewing the intro.
      if (r && !loggedIn) setRole(r);
      setOnboarded(true);
      if (obPreview) {
        setObPreview(false);
        try { window.history.replaceState({}, document.title, window.location.pathname + window.location.hash); } catch (_) {}
      }
    }} /></Ctx.Provider>;
  }
  if (!loggedIn) return <Ctx.Provider value={ctxVal}>{IS_DESKTOP ? <React.Suspense fallback={null}><DesktopLogin onBrowse={() => { setDesktopView("browse"); setShowLogin(false); }} /></React.Suspense> : <LoginPage />}</Ctx.Provider>;

  // Social auth users (role="user") → browse with profile context, no sync needed
  if (role === "user") return <Ctx.Provider value={ctxVal}><PublicBrowsePage onBack={doLogout} isLoggedIn={true} /></Ctx.Provider>;

  if (loggedIn && isSyncing) {
    // Post-auth sync wait — can be up to ~50s on Render's free-tier
    // cold start. ColdStartLoader is a richer experience than the
    // old static spinner: brand mark with breathing halo, asymptotic
    // progress bar, step-by-step narrative, and a reassurance footer
    // that frames the wait as a one-time first-load thing.
    return (
      <Ctx.Provider value={ctxVal}>
        <ColdStartLoader h={ctxVal.h} fullscreen label="Authenticating" />
      </Ctx.Provider>
    );
  }

  // ── Desktop chrome (ProRentz, logged in) ──────────────────────────
  // Left sidebar nav + a wide, centered content canvas, reusing every
  // existing page component and Context untouched — only the frame
  // changes (no 430px cap, no fixed bottom bar, no pull-to-refresh).
  // Admin keeps the mobile-style render (AdminPage has its own nav).
  // Tours are skipped here (they anchor to the mobile bottom bar).
  if (IS_DESKTOP && role !== "admin") {
    return <Ctx.Provider value={ctxVal}>
      <div style={{ display: "flex", minHeight: "100vh", background: ctxVal.h.bg, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif" }}>
        <DesktopSidebar />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <TopBar />
          <div style={{ flex: 1, padding: "28px 24px 48px", display: "flex", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: 1080 }}>
              <CampaignDisplay />
              {role === "landlord" ? <LandlordPage /> : role === "service" ? <ServicePage /> : <TenantPage />}
            </div>
          </div>
        </div>
        {role === "landlord" && <SharePropertiesModal />}
        {appSettings.feedback_button_enabled !== false && <FeedbackButton />}
        {notif && <Noti m={notif.m} type={notif.type} h={ctxVal.h} />}
        {confirmAction && <ConfirmModal h={ctxVal.h} t={ctxVal.t} message={confirmAction.message} onConfirm={() => { confirmAction.onConfirm(); closeConfirm(); }} onCancel={closeConfirm} />}
      </div>
    </Ctx.Provider>;
  }

  return <Ctx.Provider value={ctxVal}><div {...pullHandlers} style={{ background: ctxVal.h.bg, minHeight: "100vh", maxWidth: 430, margin: "0 auto", fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif", position: "relative" }}>
    {pulling && <div style={{ textAlign: "center", padding: `${Math.min(pullY / 2, 30)}px 0 8px`, transition: pulling ? "none" : "all 0.3s", overflow: "hidden" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ctxVal.h.ac} strokeWidth="2" style={{ animation: pullY > 50 ? "spin 1s linear infinite" : "none", opacity: pullY / 80 }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>}
    <TopBar /><div style={{ padding: "0 12px 74px" }}>{role !== "admin" && <CampaignDisplay />}{role === "landlord" ? <LandlordPage /> : role === "admin" ? <AdminPage /> : role === "service" ? <ServicePage /> : <TenantPage />}</div><BottomBar />{role === "landlord" && <SharePropertiesModal />}{appSettings.feedback_button_enabled !== false && <FeedbackButton />}{notif && <Noti m={notif.m} type={notif.type} h={ctxVal.h} />}
    {confirmAction && <ConfirmModal h={ctxVal.h} t={ctxVal.t} message={confirmAction.message} onConfirm={() => { confirmAction.onConfirm(); closeConfirm(); }} onCancel={closeConfirm} />}
    {/* Product tour — first-time onboarding for landlord / tenant /
        service-person. Skipped for admin (super-admin doesn't need
        an FTUE walk-through). Stored per user id so returning
        users never see it twice. */}
    {ftueRole && ftue.isOpen && (
      <ProductTour
        open={ftue.isOpen}
        steps={stepsForRole(ftueRole, ctxVal.h, ctxVal.t)}
        onComplete={ftue.complete}
        onSkip={ftue.skip}
        h={ctxVal.h}
        t={ctxVal.t}
      />
    )}
    {/* Coach-mark tour — only opens after the modal tour above
        finishes (useCoachMarkTour gates on the ftue_<role>_<id>
        flag). Walks the user through the bottom nav and gear
        icon with anchored tooltips and a spotlight cutout. */}
    {ftueRole && coachTour.isOpen && !ftue.isOpen && (
      <CoachMarkTour
        open={coachTour.isOpen}
        steps={coachStepsForRole(ftueRole)}
        onComplete={coachTour.complete}
        onSkip={coachTour.skip}
        h={ctxVal.h}
      />
    )}
    {/* Per-page coach mark tour. Auto-launches the first time the
        user visits each section. Gated on:
          - The main FTUE being complete (no triple-stacking)
          - The bottom-nav coach tour being closed (one at a time)
          - The page having step content registered */}
    {ftueRole && pageCoach.isOpen && !ftue.isOpen && !coachTour.isOpen && currentPageSteps.length > 0 && (
      <CoachMarkTour
        open={pageCoach.isOpen}
        steps={currentPageSteps}
        onComplete={pageCoach.complete}
        onSkip={pageCoach.skip}
        h={ctxVal.h}
      />
    )}
  </div></Ctx.Provider>;
}
